// src/routes/global.tsx
import React from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { EEntity, EModule } from "@/components/table/filter/useTables";

const PageUsers = React.lazy(() => import("@/modules/global/pages/users").then(m => ({ default: m.PageUsers })));
const PageProfiles = React.lazy(() => import("@/modules/global/pages/profiles").then(m => ({ default: m.PageProfiles })));
const PageAudits = React.lazy(() => import("@/modules/global/pages/audits").then(m => ({ default: m.PageAudits })));
const PageJob = React.lazy(() => import("@/modules/global/pages/jobs/id").then(m => ({ default: m.PageJob })));
const PageApiKeys = React.lazy(() => import("@/modules/global/pages/apikeys").then(m => ({ default: m.PageApiKeys })));
const PageFieldCatalogPermissions = React.lazy(() => import("@/modules/global/pages/fieldCatalogPermissions").then(m => ({ default: m.PageFieldCatalogPermissions })));
const PageAiPathologyAnalyses = React.lazy(() => import("@/modules/global/pages/aiPathologyAnalyses").then(m => ({ default: m.PageAiPathologyAnalyses })));
const PageTeamUsers = React.lazy(() => import("@/modules/towermanagement/pages/teamUsers").then(m => ({ default: m.PageTeamUsers })));
const PageAiPathologySchedules = React.lazy(() => import("@/modules/global/pages/aiPathologySchedules").then(m => ({ default: m.PageAiPathologySchedules })));

// ----------------------------------------------
// Helper: embrulha no ProtectedRoute (GLOBAL)
// ----------------------------------------------
type Def = {
  path: string;
  element: React.ReactElement;
  auth: boolean;           // mantido p/ consistência (todas aqui são true)
  entity: EEntity;
};

const wrap = ({ auth, entity, element }: Omit<Def, "path">) => (
  <React.Suspense fallback={<>Carregando...</>}>
    <ProtectedRoute
      auth={auth}
      {...(auth ? { module: EModule.GLOBAL, entity } : {})}
    >
      {element}
    </ProtectedRoute>
  </React.Suspense>
);

const users: Def[] = [
  { path: "/global/users", auth: true, entity: EEntity.GBL_USERS, element: <PageUsers /> },
];

const profiles: Def[] = [
  { path: "/global/profiles", auth: true, entity: EEntity.GBL_PROFILES, element: <PageProfiles /> },
];

const jobs: Def[] = [
  { path: "/global/jobs/:idJob", auth: true, entity: EEntity.GBL_JOBS, element: <PageJob /> },
];

const audits: Def[] = [
  { path: "/global/audits", auth: true, entity: EEntity.GBL_AUDITS, element: <PageAudits /> },
];

const apiKeys: Def[] = [
  { path: "/global/apikeys", auth: true, entity: EEntity.GBL_APIKEYS, element: <PageApiKeys /> },
  { path: "/global/apikeys/:idApiKey", auth: true, entity: EEntity.GBL_APIKEYS, element: <PageApiKeys /> },
];

const fieldCatalogPermissions: Def[] = [
  {
    path: "/global/field-catalog-permissions",
    auth: true,
    entity: EEntity.GBL_FIELD_CATALOG_PERMISSIONS,
    element: <PageFieldCatalogPermissions />,
  },
];

const teamUsers: Def[] = [
  {
    path: "/global/team_users",
    auth: true,
    entity: EEntity.GBL_USERS,
    element: <PageTeamUsers />,
  },
];

const aiPathology: Def[] = [
  {
    path: "/global/aiPathologyAnalyses",
    auth: true,
    entity: EEntity.GBL_AIPATHOLOGYANALYSES,
    element: <PageAiPathologyAnalyses />,
  },
  {
    path: "/global/aiPathologyAnalyses/:idAnalysis",
    auth: true,
    entity: EEntity.GBL_AIPATHOLOGYANALYSES,
    element: <PageAiPathologyAnalyses />,
  },
  {
    path: "/global/aiPathologySchedules",
    auth: true,
    entity: EEntity.GBL_AIPATHOLOGYSCHEDULES,
    element: <PageAiPathologySchedules />,
  },
  {
    path: "/global/aiPathologySchedules/:idSchedule",
    auth: true,
    entity: EEntity.GBL_AIPATHOLOGYSCHEDULES,
    element: <PageAiPathologySchedules />,
  },
];

// ----------------------------------------------
// Exporta as <Route> prontas
// ----------------------------------------------
const defs: Def[] = [
  ...users,
  ...profiles,
  ...jobs,
  ...audits,
  ...apiKeys,
  ...fieldCatalogPermissions,
  ...teamUsers,
  ...aiPathology,
];

export const globalRoutes = defs.map(({ path, ...rest }) => (
  <Route key={path} path={path} element={wrap(rest)} />
));
